import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RubleIcon } from "@/shared/icons";
import { SoundButton } from "@/shared/ui";
import { Checkbox } from "@/shared/ui";
import { TextInput } from "@/shared/ui";
import { inventoryByIdSelector, setMemoizedItem } from "@/domains/inventory/slice";
import useTradeManager from "@/domains/inventory/hooks/trade/useTradeManager";
import useTradeParticipants from "@/domains/inventory/hooks/trade/useTradeParticipants";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventoryTradeSelfSuggestionHeader from "@/domains/inventory/components/trade/InventoryTradeSelfSuggestionHeader";

type Props = { currCharacterId: number };

export default function InventoryTradeSelfSuggestionContainer({ currCharacterId }: Props) {
  const dispatch = useAppDispatch();
  const { tradeParticipant, trade } = useTradeParticipants(currCharacterId);
  const inventory = useAppSelector((state) =>
    inventoryByIdSelector(state, tradeParticipant?.self?.inventory.id),
  );
  const [isReady, setIsReady] = useState(false);
  const { addItemToTrade, setReady, confirmTrade, cancelTrade, setCashOffer } = useTradeManager();
  const [money, setMoney] = useState("");

  const onClose = useCallback(() => {
    if (trade?.tradeId) {
      cancelTrade({ tradeId: Number(trade.tradeId) });
    }
  }, [cancelTrade, trade?.tradeId]);

  const onReady = useCallback(() => {
    if (trade?.tradeId) {
      setReady({
        tradeId: Number(trade.tradeId),
        isReady: !isReady,
      });
    }
  }, [trade?.tradeId, isReady]);

  useEffect(() => {
    if (trade?.tradeId) {
      setCashOffer({
        amount: Number(money || 0),
        tradeId: Number(trade.tradeId),
      });
    }
  }, [money, trade?.tradeId]);

  useEffect(() => {
    if (tradeParticipant?.self) {
      setIsReady(tradeParticipant.self.isReady);
    }
  }, [tradeParticipant]);

  const onConfirm = useCallback(() => {
    confirmTrade({ tradeId: Number(trade.tradeId) });
  }, [trade]);

  if (!inventory || !trade) return null;

  return (
    <InventoryWrapper className="max-h-[42rem]">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryTradeSelfSuggestionHeader
          onClose={onClose}
          name={tradeParticipant.self.name}
          id={tradeParticipant.self.characterId}
        />
      </InventoryHeader>
      <InventoryContent className="py-5 text-18 font-medium">
        Ваши предметы для обмена
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto">
        <InventoryGrid
          tradeId={trade.tradeId}
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          items={inventory.items}
          onAddItemToTrade={addItemToTrade}
          onMemoizedItem={(item) => dispatch(setMemoizedItem(item))}
          disabled={isReady}
        />
      </InventoryContent>
      <InventoryContent className="py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <RubleIcon size={18} />
          <div className="flex items-center gap-1 text-14 font-bold text-white/50">
            Доплата:{" "}
            {isReady ? (
              <span className="text-15 font-bold text-white">
                {formatPrice(tradeParticipant.self.cashOffer)}
              </span>
            ) : (
              <TextInput
                value={money}
                onChange={setMoney}
                type="number"
                className="text-right"
                inputClassName="w-[6rem] h-[2rem] px-2"
              />
            )}
            <span className="text-15 font-bold text-white">₽</span>
          </div>
        </div>
        <div
          className={clsx(
            "flex items-center gap-2 px-2 border border-[#191919] rounded-[0.5rem]",
            "h-[2rem] text-15 font-medium",
          )}
          data-mz-accent="success"
        >
          <Checkbox checked={isReady} onChange={onReady} variant="variant-2" />
          Готов к обмену
        </div>
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight weight={tradeParticipant.self.totalWeight} />
        <SoundButton
          data-mz-accent="success"
          className="h-[2.375rem]"
          onClick={onConfirm}
          disabled={!tradeParticipant.other.isReady || !isReady}
        >
          Совершить обмен
        </SoundButton>
      </InventoryFooter>
    </InventoryWrapper>
  );
}
